import { Anchor } from "@mantine/core";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { AuthFlowScaffold } from "../auth/components/AuthFlowScaffold";
import { useAuthFlow } from "../auth/hooks/useAuthFlow";
import { safeReturnTo } from "../auth/rules/returnTo";
import { clearOnboardingIntent } from "../onboarding/rules/intent";

/**
 * loginSearchSchema validates the /login search params (Zod-first). flow is the
 * Ory flow id from the browser-init 303; the rest are forwarded init signals.
 */
const loginSearchSchema = z.object({
  aal: z.literal("aal2").optional(),
  flow: z.string().optional(),
  login_challenge: z.string().optional(),
  refresh: z.boolean().optional(),
  return_to: z.string().optional(),
});

/**
 * Route is /login. It initializes (or resumes) an Ory login browser flow and
 * renders it generically. A re-rendered flow (AAL2 second factor) flows through
 * the same form. Cross-links to registration + recovery.
 */
export const Route = createFileRoute("/_auth/login")({
  beforeLoad: ({ preload }) => {
    // A login is not a fresh registration, so it must not inherit a stale
    // create-organization intent a prior abandoned signup may have left pending — clear it
    // on a real load (the !preload guard skips a hover prefetch, which must not mutate it).
    if (!preload) {
      clearOnboardingIntent();
    }
  },
  component: LoginRoute,
  validateSearch: loginSearchSchema,
});

function LoginRoute() {
  const { t } = useTranslation("iam");
  const search = Route.useSearch();
  const state = useAuthFlow("login", {
    defaultRedirect: safeReturnTo(search.return_to),
    flowId: search.flow,
    initOptions: {
      aal: search.aal,
      loginChallenge: search.login_challenge,
      refresh: search.refresh,
    },
  });

  return (
    <AuthFlowScaffold state={state} subtitle={t("login.subtitle")} title={t("login.title")}>
      <Anchor component={Link} size="sm" to="/registration">
        {t("login.createAccount")}
      </Anchor>
      <Anchor component={Link} size="sm" to="/recovery">
        {t("login.forgotPassword")}
      </Anchor>
    </AuthFlowScaffold>
  );
}
